import type { JSONContent } from '@tiptap/react';

/**
 * HTML ↔ TipTap inline-content conversion for richtext blocks (ported from Scilora).
 *
 * The richtextBlock node holds `inline*` content (text + marks + hardBreak) while the
 * backend stores `data.html`. Paragraphs and the supported marks (bold, italic,
 * underline, strike, link) round-trip losslessly; lists degrade to visible
 * "• " / "1. " prefixed lines once a block is edited (documented lossy case).
 */

interface InlineMark {
    type: string;
    attrs?: Record<string, unknown>;
}

const MARK_TAGS: Record<string, string> = {
    strong: 'bold',
    b: 'bold',
    em: 'italic',
    i: 'italic',
    u: 'underline',
    s: 'strike',
    strike: 'strike',
    del: 'strike',
};

const LINE_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'tr']);

export function htmlToInline(html: string): JSONContent[] {
    if (!html || html.trim() === '') {
        return [];
    }

    const body = new DOMParser().parseFromString(html, 'text/html').body;
    const lines: JSONContent[][] = [];
    let current: JSONContent[] = [];

    const endLine = (): void => {
        if (current.length > 0) {
            lines.push(current);
            current = [];
        }
    };

    const pushText = (raw: string, marks: InlineMark[]): void => {
        // #526: only line breaks / tabs of pretty-printed markup collapse — runs of
        // spaces the author typed survive (stored as NBSP pairs, see inlineToHtml).
        const text = raw.replace(/\s*[\r\n]\s*/g, ' ').replace(/[\t\f\v]+/g, ' ').replace(/ /g, ' ');
        if (text.trim() === '' && (current.length === 0 || /\n/.test(raw))) {
            return;
        }
        if (text === '') {
            return;
        }
        current.push(
            marks.length > 0
                ? { type: 'text', text, marks: marks.map((m) => ({ ...m })) }
                : { type: 'text', text },
        );
    };

    const visit = (node: Node, marks: InlineMark[]): void => {
        if (node.nodeType === Node.TEXT_NODE) {
            pushText(node.textContent ?? '', marks);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const el = node as Element;
        const tag = el.tagName.toLowerCase();

        if (tag === 'br') {
            endLine();
            return;
        }

        if (tag === 'ul' || tag === 'ol') {
            endLine();
            const items = Array.from(el.children).filter((child) => child.tagName.toLowerCase() === 'li');
            items.forEach((li, index) => {
                pushText(tag === 'ol' ? `${index + 1}. ` : '• ', marks);
                Array.from(li.childNodes).forEach((child) => visit(child, marks));
                endLine();
            });
            return;
        }

        const markType = MARK_TAGS[tag];
        const nextMarks =
            tag === 'a'
                ? [...marks, { type: 'link', attrs: { href: el.getAttribute('href') ?? '' } }]
                : markType
                    ? [...marks, { type: markType }]
                    : marks;

        Array.from(el.childNodes).forEach((child) => visit(child, nextMarks));

        if (LINE_TAGS.has(tag)) {
            endLine();
        }
    };

    Array.from(body.childNodes).forEach((child) => visit(child, []));
    endLine();

    const content: JSONContent[] = [];
    lines.forEach((line, index) => {
        if (index > 0) {
            content.push({ type: 'hardBreak' });
        }
        content.push(...line);
    });
    return content;
}

export function inlineToHtml(content: JSONContent[] | undefined): string {
    if (!content || content.length === 0) {
        return '';
    }

    const lines: string[] = [];
    let buffer = '';
    for (const node of content) {
        if (node.type === 'hardBreak') {
            lines.push(buffer);
            buffer = '';
            continue;
        }
        if (node.type === 'text' && typeof node.text === 'string') {
            buffer += textNodeToHtml(node.text, node.marks ?? []);
        }
    }
    lines.push(buffer);

    return lines
        .filter((line) => line.trim() !== '')
        .map((line) => `<p>${line}</p>`)
        .join('');
}

const MARK_ORDER = ['link', 'bold', 'italic', 'underline', 'strike'];

function textNodeToHtml(text: string, marks: { type?: string; attrs?: Record<string, unknown> }[]): string {
    let html = escapeHtml(text);
    const sorted = [...marks].sort((a, b) => MARK_ORDER.indexOf(b.type ?? '') - MARK_ORDER.indexOf(a.type ?? ''));
    for (const mark of sorted) {
        switch (mark.type) {
            case 'bold':
                html = `<strong>${html}</strong>`;
                break;
            case 'italic':
                html = `<em>${html}</em>`;
                break;
            case 'underline':
                html = `<u>${html}</u>`;
                break;
            case 'strike':
                html = `<s>${html}</s>`;
                break;
            case 'link': {
                const href = escapeAttribute(String(mark.attrs?.href ?? ''));
                html = `<a href="${href}">${html}</a>`;
                break;
            }
            default:
                break;
        }
    }
    return html;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // #526: browsers collapse consecutive spaces; keep the first one breakable
        // and turn the rest into NBSP so "a   b" renders (and reloads) as typed.
        .replace(/ {2,}/g, (run) => ' ' + ' '.repeat(run.length - 1));
}

function escapeAttribute(value: string): string {
    return escapeHtml(value).replace(/"/g, '&quot;');
}
